import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import Header from '../components/Header';
import { listVariables, createVariable, updateVariable, deleteVariable } from '../services/api';

interface Variable {
  variable_id: string;
  variable_name: string;
  description: string;
  default_value?: string;
  is_system: boolean;
  created_at: string;
  created_by: string;
  status: string;
}

const VariableRegistry: React.FC = () => {
  const [variables, setVariables] = useState<Variable[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    variable_name: '',
    description: '',
    default_value: ''
  });

  useEffect(() => {
    loadVariables();
  }, []);

  const loadVariables = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listVariables();
      setVariables(data.variables || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load variables');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setEditMode(false);
    setEditingId(null);
    setFormData({
      variable_name: '',
      description: '',
      default_value: ''
    });
    setError(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (variable: Variable) => {
    setEditMode(true);
    setEditingId(variable.variable_id);
    setFormData({
      variable_name: variable.variable_name,
      description: variable.description,
      default_value: variable.default_value || ''
    });
    setError(null);
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    setError(null);

    if (!formData.variable_name.trim()) {
      setError('Variable name is required');
      return;
    }

    if (!formData.description.trim()) {
      setError('Description is required');
      return;
    }

    try {
      if (editMode && editingId) {
        await updateVariable(editingId, formData);
      } else {
        await createVariable(formData);
      }
      setDialogOpen(false);
      setFormData({
        variable_name: '',
        description: '',
        default_value: ''
      });
      loadVariables();
    } catch (err: any) {
      setError(err.message || `Failed to ${editMode ? 'update' : 'create'} variable`);
    }
  };

  const handleDelete = async (variable: Variable) => {
    if (!window.confirm(`Delete variable "${variable.variable_name}"?\n\nThis will make it invalid in all templates using it.`)) {
      return;
    }

    try {
      await deleteVariable(variable.variable_id);
      loadVariables();
    } catch (err: any) {
      setError(err.message || 'Failed to delete variable');
    }
  };

  const formatDate = (isoString: string) => {
    return new Date(isoString).toLocaleString();
  };

  // Sort variables: system first, then by name
  const sortedVariables = [...variables].sort((a, b) => {
    if (a.is_system && !b.is_system) return -1;
    if (!a.is_system && b.is_system) return 1;
    return a.variable_name.localeCompare(b.variable_name);
  });

  return (
    <>
      <Header />
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Variable Registry
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
          >
            Add Variable
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Variables define placeholders available in all prompt templates. Variables can be added, edited, and removed as needed.
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          A variable name here must match a field that actually exists in the scoring request's JSON body.
          For fields inside nested JSON, use dot notation matching the request structure (e.g. <code>student.grade</code>,{' '}
          <code>metadata.district.isn</code>). Registering a variable does not create the field — it only makes the name
          usable in templates.
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Variable Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Default Value</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedVariables.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No variables defined
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedVariables.map((v) => (
                      <TableRow key={v.variable_id}>
                        <TableCell>
                          <code style={{
                            backgroundColor: '#f5f5f5',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontFamily: 'monospace'
                          }}>
                            {`{${v.variable_name}}`}
                          </code>
                        </TableCell>
                        <TableCell>{v.description}</TableCell>
                        <TableCell>
                          {v.default_value ? (
                            <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                              {v.default_value.length > 50
                                ? v.default_value.substring(0, 50) + '...'
                                : v.default_value}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.disabled">
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{formatDate(v.created_at)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Edit variable">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenEdit(v)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete variable">
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(v)}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{editMode ? 'Edit Variable' : 'Add Variable'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField
                label="Variable Name"
                value={formData.variable_name}
                onChange={(e) => setFormData({ ...formData, variable_name: e.target.value })}
                helperText="Alphanumeric and underscores only. Must match a field in the request JSON body (use dot notation for nested fields, e.g. student.grade)."
                fullWidth
                required
                disabled={editMode}  // Can't change name when editing
              />
              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                fullWidth
                multiline
                rows={2}
                required
                helperText="Describe what this variable represents"
              />
              <TextField
                label="Default Value (Optional)"
                value={formData.default_value}
                onChange={(e) => setFormData({ ...formData, default_value: e.target.value })}
                fullWidth
                multiline
                rows={2}
                helperText="Fallback value if not provided at runtime"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained" color="primary">
              {editMode ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};

export default VariableRegistry;
